import { authErrorMessage } from "@auth/authErrorMessage";
import { deriveSubdomainFromLocation } from "@auth/subdomain";
import { AuthClipboard } from "@components/AuthClipboard";
import { useAuth } from "@context/AuthContext";
import { Button, Input, NameTag } from "@scribenote/brian-design-system";
import { useEffect, useRef, useState, type SubmitEvent } from "react";

const GENERIC_ERROR = "Incorrect password. Please try again.";

export const LoginScreen = () => {
  const { login } = useAuth();
  // Deployed: the clinic is the host's subdomain. Localhost: there's none, so
  // we collect it via the field below. Either way it's sent in the login body.
  const derivedSubdomain = deriveSubdomainFromLocation();
  const needsSubdomain = derivedSubdomain === null;

  const [subdomain, setSubdomain] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const subdomainRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (needsSubdomain ? subdomainRef : passwordRef).current?.focus();
  }, [needsSubdomain]);

  const effectiveSubdomain = (derivedSubdomain ?? subdomain)
    .trim()
    .toLowerCase();

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (!effectiveSubdomain || !password || submitting) return;
    setSubmitting(true);
    try {
      await login(effectiveSubdomain, password);
    } catch (err) {
      setError(authErrorMessage(err, GENERIC_ERROR));
      setPassword("");
      passwordRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  const clearError = () => {
    if (error) setError(null);
  };

  return (
    <AuthClipboard>
      {/* The letterhead: Brian introduces himself before asking for anything. */}
      <div className="flex justify-center">
        <NameTag className="w-full max-w-56 text-[15px] sm:text-base" />
      </div>

      <div className="mt-6 text-center">
        <h1 className="font-display text-xl font-medium tracking-tight text-ink">
          Sign in to Brian
        </h1>
        <p className="mt-1 text-sm text-ink-body">
          I just need your clinic password.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
        {needsSubdomain && (
          <Input
            ref={subdomainRef}
            id="clinic-subdomain"
            type="text"
            label="Clinic subdomain"
            autoComplete="off"
            value={subdomain}
            onChange={(e) => {
              setSubdomain(e.target.value);
              clearError();
            }}
            placeholder="demo"
            disabled={submitting}
            data-testid="login-subdomain"
          />
        )}
        <Input
          ref={passwordRef}
          id="clinic-password"
          type="password"
          label="Clinic password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            clearError();
          }}
          placeholder="••••"
          error={error ?? undefined}
          disabled={submitting}
          data-testid="login-password"
        />

        <Button
          type="submit"
          variant="primary"
          busy={submitting}
          disabled={!effectiveSubdomain || !password}
          data-testid="login-submit"
          className="mt-1 w-full"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </Button>
      </form>
    </AuthClipboard>
  );
};
